import { useQuery } from "@tanstack/react-query";
import {
  Button,
  DatePicker,
  Form,
  Input,
  InputNumber,
  message,
  Radio,
  Select,
  Space,
} from "antd";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  createCampaign,
  getAllRewards,
  getCampaign,
  getSchemas,
  updateCampaign,
} from "../actions/acc_actions";
import ContentLayout from "../components/layout/ContentLayout";
import RewardsFormModal from "../components/RewardFromModal";
import Loading from "../utils/Loading";

function getOffset(timezone) {
  const now = new Date();
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    hour12: false,
    timeZoneName: "shortOffset",
  });
  const parts = formatter.formatToParts(now);
  const offsetPart = parts.find((p) => p.type === "timeZoneName")?.value;

  return offsetPart || "";
}

export default function CampaignsForm() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [isRewardModalOpen, setRewardModalOpen] = useState(false);

  const [targetValue, setTargetValue] = useState("all");
  const [notificationValue, setNotificationValue] = useState("standard");
  const accUrn = useSelector((state) => state.account.urn);
  const businessUrn = useSelector((state) => state.account.businessUrn);

  const { isLoading: campaignLoading, data: campaignData } = useQuery({
    queryKey: ["campaign"],
    queryFn: () => getCampaign(id),
    enabled: !!id,
  });

  const { isLoading: schemasLoading, data: schemasData } = useQuery({
    queryKey: ["schemas", businessUrn],
    // queryFn: () => getSchemas(businessUrn, "VOUCHER"),
    queryFn: () => getSchemas(businessUrn, "VOUCHER"),
    enabled: !!businessUrn,
  });

  const {
    isLoading: rewardsLoading,
    data: rewardsData,
    refetch: refetchRewards,
  } = useQuery({
    queryKey: ["rewards", businessUrn],
    queryFn: () => getAllRewards(businessUrn),
    enabled: !!businessUrn,
  });

  const rewardOptions = [
    ...(rewardsData?.body?.map((item) => ({
      label: item.name,
      value: item.urn,
    })) || []),
    { label: t("add_new_reward"), value: "__add_new__" },
  ];

  const timeZones = Intl.supportedValuesOf("timeZone", { region: "all" });

  useEffect(() => {
    if (campaignData?.body && id) {
      const campaign = campaignData.body;
      form.setFieldsValue({
        stamp_count: campaign.stamp_count,
        voucher_loyalty_scheme_urn: campaign.voucher_loyalty_scheme_urn,
        reward_type_urn: campaign.reward_type_urn,
        target_timezone: campaign.target_timezone,
        deliver_at: dayjs(campaign.deliver_at),
        expire_at: dayjs(campaign.expire_at),
        campaign_name: campaign.campaign_name,
        promotion_urn: campaign.promotion_urn,
        description: campaign.description,
        note: campaign.note,
        min_rewards_earned: campaign.min_rewards_earned,
        lapsed_exclude_from: campaign.lapsed_exclude_from
          ? dayjs(campaign.lapsed_exclude_from)
          : null,
      });

      setTargetValue(campaign.target_segment);
      setNotificationValue(campaign.notification_delivery_type);
    }
  }, [campaignData, id, form]);

  if (schemasLoading || rewardsLoading || campaignLoading) {
    return <Loading />;
  }

  const onChange = (e) => setTargetValue(e.target.value);
  const onNotificationChange = (e) => setNotificationValue(e.target.value);

  const onFinish = (values) => {
    const formData = {
      ...values,
      account_urn: accUrn,
      business_urn: businessUrn,
      target_segment: targetValue,
      notification_delivery_type: notificationValue,
      deliver_at: values.deliver_at.toISOString(),
      expire_at: values.expire_at.toISOString(),
      lapsed_exclude_from: values?.lapsed_exclude_from?.toISOString(),
    };
    setLoading(true);

    const successMessage = campaignData
      ? t("campaign_updated_success")
      : t("campaign_created_success");

    const errorMessage = t("something_went_wrong");

    const request = campaignData
      ? updateCampaign(id, formData)
      : createCampaign(formData);

    request
      .then(() => {
        navigate("/campaigns");
        message.success(successMessage);
      })
      .catch((err) => {
        message.error(err?.response?.data?.message || errorMessage);
      })
      .finally(() => setLoading(false));
  };

  const disabledDate = (current) => current && current < dayjs().endOf("day");

  return (
    <ContentLayout
      header={
        <>
          <h1>
            {t("campaign_setup_new")} <span>{t("campaign_outlets")}</span>
          </h1>
          <p className="content_layout-subtitle">
            {t("campaign_outlets_setup")}
          </p>
        </>
      }
    >
      <Form
        form={form}
        className="rewards-form"
        onFinish={onFinish}
        layout="vertical"
      >
        <div className="rewards-form-section">
          <h4>{t("basic")}</h4>
          <div className="rewards-inputs">
            <Form.Item
              name="voucher_loyalty_scheme_urn"
              label={t("campaign_business_question")}
              rules={[{ required: true }]}
            >
              <Select
                options={schemasData?.data?.body.map((item) => ({
                  label: item.name,
                  value: item.urn,
                }))}
              />
            </Form.Item>

            <Form.Item
              name="reward_type_urn"
              label={t("campaign_select_reward")}
              rules={[{ required: true }]}
            >
              <Select
                options={rewardOptions}
                onChange={(value) => {
                  if (value === "__add_new__") {
                    setRewardModalOpen(true);
                    form.setFieldsValue({ reward_type_urn: null });
                  }
                }}
              />
            </Form.Item>

            <Form.Item
              rules={[{ required: true }]}
              label={t("time_zone")}
              name="target_timezone"
            >
              <Select
                showSearch
                placeholder={t("select_timezone")}
                optionFilterProp="children"
                filterOption={(input, option) =>
                  option?.children.toLowerCase().includes(input.toLowerCase())
                }
                style={{ width: "100%" }}
              >
                {timeZones.map((zone) => {
                  const city = zone.includes("/")
                    ? zone.split("/")[1].replace(/_/g, " ")
                    : zone;
                  const offset = getOffset(zone);
                  return (
                    <Select.Option key={zone} value={zone}>
                      {`${city} (${offset})`}
                    </Select.Option>
                  );
                })}
              </Select>
            </Form.Item>

            <Form.Item
              name="deliver_at"
              label={t("deliver_at")}
              rules={[{ required: true }]}
            >
              <DatePicker
                format="YYYY-MM-DD HH:mm:ss"
                disabledDate={disabledDate}
                showTime={{ defaultValue: dayjs("00:00:00", "HH:mm:ss") }}
              />
            </Form.Item>

            <Form.Item
              name="expire_at"
              label={t("expire_at")}
              dependencies={["deliver_at"]}
              rules={[
                { required: true },
                ({ getFieldValue }) => ({
                  validator: (_, value) =>
                    !value || !getFieldValue("deliver_at") || value.isAfter(getFieldValue("deliver_at"))
                      ? Promise.resolve()
                      : Promise.reject(new Error(t("expire_after_deliver"))),
                }),
              ]}
            >
              <DatePicker
                format="YYYY-MM-DD HH:mm:ss"
                disabledDate={disabledDate}
                showTime={{ defaultValue: dayjs("00:00:00", "HH:mm:ss") }}
              />
            </Form.Item>
          </div>
        </div>

        <div className="rewards-form-section">
          <h4>{t("campaign")}</h4>
          <div className="rewards-inputs">
            <Form.Item rules={[{ required: true }]} label={t("target_segment")}>
              <Radio.Group onChange={onChange} value={targetValue}>
                <Space direction="vertical">
                  <Radio value="all">{t("segment_all")}</Radio>
                  <Radio value="has-stamps">{t("segment_stamps_earned")}</Radio>
                  <Radio value="lapsed">{t("segment_lapsed")}</Radio>
                  <Radio value="rewards-earned">
                    {t("segment_rewards_earned")}
                  </Radio>
                </Space>
              </Radio.Group>
            </Form.Item>

            {targetValue === "has-stamps" && (
              <Form.Item
                name="stamp_count"
                label={t("no_of_stamps")}
                rules={[
                  { required: true, message: t("no_of_stamps_required") },
                ]}
              >
                <InputNumber min={1} />
              </Form.Item>
            )}

            {targetValue === "lapsed" && (
              <Form.Item
                name="lapsed_exclude_from"
                label={t("lapsed_exclude_from")}
                rules={[{ required: true, message: t("expire_at_required") }]}
              >
                <DatePicker
                  format="YYYY-MM-DD HH:mm:ss"
                  showTime={{ defaultValue: dayjs("00:00:00", "HH:mm:ss") }}
                />
              </Form.Item>
            )}

            {targetValue === "rewards-earned" && (
              <Form.Item
                rules={[{ required: true }]}
                name="min_rewards_earned"
                label={t("min_rewards_earned")}
              >
                <InputNumber min={1} />
              </Form.Item>
            )}
          </div>
        </div>

        <div className="rewards-form-section">
          <h4>{t("notification")}</h4>
          <div className="rewards-inputs">
            <Form.Item
              name="alert"
              label={t("notification_alert")}
              rules={[{ required: true }]}
            >
              <Radio.Group
                onChange={onNotificationChange}
                value={notificationValue}
                defaultValue={notificationValue}
              >
                <Space direction="vertical">
                  <Radio value="standard">{t("notification_standard")}</Radio>
                  <Radio value="delayed-last-active">
                    {t("notification_intelligent")}
                  </Radio>
                </Space>
              </Radio.Group>
            </Form.Item>

            <Form.Item required name="note" label={t("note_anything")}>
              <Input.TextArea style={{ resize: "none" }} rows={6} />
            </Form.Item>

            {/* The message is saved into promotion.description, which holds at most 512 characters. */}
            <Form.Item
              name="description"
              label={t("voucher_message")}
              rules={[
                { required: true, whitespace: true, message: t("voucher_message_required") },
                { max: 512, message: t("voucher_message_max") },
              ]}
            >
              <Input.TextArea style={{ resize: "none" }} rows={6} count={{ show: true, max: 512 }} />
            </Form.Item>
          </div>
        </div>

        <Form.Item className="rewards-form-btn">
          <Button htmlType="submit" loading={loading} type="primary">
            {id ? t("update") : t("submit")}
          </Button>
        </Form.Item>
      </Form>

      <RewardsFormModal
        visible={isRewardModalOpen}
        onClose={() => setRewardModalOpen(false)}
        onSuccess={() => {
          refetchRewards();
          setRewardModalOpen(false);
        }}
      />
    </ContentLayout>
  );
}
